import {
  buildHighlightSearchCandidates,
  normalizeQuotesForHighlightMatch,
  stripMarkdownForHighlightSnippet,
} from "../shared/agentHighlightSnippet.js";

const EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR = "\n";

function buildEditorPlainText(doc) {
  return doc.textBetween(0, doc.content.size, EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR);
}

function pmPositionAtEditorPlainIndex(doc, plainIndex) {
  const target = Math.max(0, Math.floor(plainIndex));
  const sep = EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR;
  const size = Math.max(0, Math.floor(doc.content.size));
  if (size === 0) return target === 0 ? 0 : null;

  let lo = 0;
  let hi = size;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const len = doc.textBetween(0, mid, sep).length;
    if (len < target) lo = mid + 1;
    else hi = mid;
  }
  if (lo > size) return null;
  return doc.textBetween(0, lo, sep).length === target ? lo : null;
}

function pmRangeForEditorPlainRange(doc, plainStart, plainEnd) {
  if (
    !Number.isFinite(plainStart)
    || !Number.isFinite(plainEnd)
    || plainEnd <= plainStart
  ) {
    return null;
  }
  const from = pmPositionAtEditorPlainIndex(doc, plainStart);
  const to = pmPositionAtEditorPlainIndex(doc, plainEnd);
  if (from == null || to == null || to <= from) return null;
  return { from, to };
}

function normalizeSnippetBlockSeparators(snippet) {
  return stripMarkdownForHighlightSnippet(String(snippet ?? ""))
    .replace(/\r\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

function findFirstTextRangeInDocBlockAware(doc, searchText) {
  if (!searchText || !doc?.textBetween) return null;

  const plain = buildEditorPlainText(doc);
  const candidates = buildHighlightSearchCandidates(searchText)
    .map((candidate) => normalizeSnippetBlockSeparators(candidate))
    .filter((candidate) => candidate.length >= 4);
  const targetPlainLen = highlightSnippetPlainLength(searchText);
  const searchCandidates = buildHighlightSearchCandidates(searchText);

  let best = null;
  let bestScore = -1;
  for (const candidate of candidates) {
    let idx = findSubstringMatch(plain, candidate)?.idx;
    let len = candidate.length;
    if (idx == null) {
      const collapsed = findCollapsedSubstringRange(plain, candidate);
      if (!collapsed) continue;
      idx = collapsed.idx;
      len = collapsed.len;
    }
    const range = pmRangeForEditorPlainRange(doc, idx, idx + len);
    if (!range) continue;
    const score = scoreRangeFitForHighlightSnippet(doc, range, targetPlainLen, searchCandidates);
    if (score > bestScore) {
      best = range;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Walk text nodes in document order and build plain text + PM position before each character.
 * When this matches `doc.textContent`, we can map grep hits to positions in O(n) total (no repeated textBetween).
 */
function buildTextAndPositions(doc) {
  const parts = [];
  const beforePos = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    const t = node.text;
    for (let i = 0; i < t.length; i++) {
      parts.push(t[i]);
      // `pos` is the position before this text node; the gap before local offset `i` is `pos + i`.
      beforePos.push(pos + i);
    }
  });
  const full = parts.join("");
  const ok = full === doc.textContent && beforePos.length === full.length;
  return { full, beforePos, ok };
}

/**
 * Find substring; optional smart-quote normalization (length-preserving).
 * @returns {{ idx: number, len: number } | null}
 */
function findSubstringMatch(full, needle, startAt = 0) {
  if (typeof full !== "string" || typeof needle !== "string") return null;
  if (!needle.length) return null;
  let idx = full.indexOf(needle, Math.max(0, startAt));
  if (idx !== -1) return { idx, len: needle.length };
  const nFull = normalizeQuotesForHighlightMatch(full);
  const nNeedle = normalizeQuotesForHighlightMatch(needle);
  if (nFull === full && nNeedle === needle) return null;
  idx = nFull.indexOf(nNeedle, Math.max(0, startAt));
  if (idx === -1) return null;
  return { idx, len: nNeedle.length };
}

function buildCollapsedSearchText(source) {
  let text = "";
  const sourceIndexByTextIndex = [];
  let previousWasWhitespace = false;
  const normalizedSource = normalizeQuotesForHighlightMatch(source);
  for (let i = 0; i < normalizedSource.length; i += 1) {
    const ch = normalizedSource[i];
    if (/\s/.test(ch)) {
      if (previousWasWhitespace) continue;
      previousWasWhitespace = true;
      text += " ";
      sourceIndexByTextIndex.push(i);
      continue;
    }
    previousWasWhitespace = false;
    text += ch;
    sourceIndexByTextIndex.push(i);
  }
  return { text, sourceIndexByTextIndex };
}

function buildPunctuationInsensitiveSearchText(source) {
  let text = "";
  const sourceIndexByTextIndex = [];
  let previousWasWhitespace = false;
  const normalizedSource = normalizeQuotesForHighlightMatch(source);
  for (let i = 0; i < normalizedSource.length; i += 1) {
    const ch = normalizedSource[i];
    if (/\s/.test(ch)) {
      if (previousWasWhitespace) continue;
      previousWasWhitespace = true;
      text += " ";
      sourceIndexByTextIndex.push(i);
      continue;
    }
    previousWasWhitespace = false;

    if (ch === "'" || ch === '"') continue;
    if (/[\p{L}\p{N}]/u.test(ch)) {
      text += ch.toLowerCase();
      sourceIndexByTextIndex.push(i);
    }
  }
  return { text, sourceIndexByTextIndex };
}

function findAnchoredLongRange(full, searchText) {
  const MIN_LONG_SNIPPET = 220;
  const ANCHOR_LEN = 200;
  if (!searchText || searchText.length < MIN_LONG_SNIPPET) return null;
  const prefix = stripMarkdownForHighlightSnippet(searchText.slice(0, ANCHOR_LEN));
  const suffix = stripMarkdownForHighlightSnippet(searchText.slice(-ANCHOR_LEN));
  if (prefix.length < 20 || suffix.length < 20) return null;

  const searchBuilders = [buildCollapsedSearchText, buildPunctuationInsensitiveSearchText];
  for (const buildSearchText of searchBuilders) {
    const fullCollapsed = buildSearchText(full);
    const prefixCollapsed = buildSearchText(prefix).text.trim();
    const suffixCollapsed = buildSearchText(suffix).text.trim();
    if (prefixCollapsed.length < 20 || suffixCollapsed.length < 20) continue;
    const startCollapsedIdx = fullCollapsed.text.indexOf(prefixCollapsed);
    if (startCollapsedIdx === -1) continue;
    const suffixSearchStart = startCollapsedIdx + prefixCollapsed.length;
    const endCollapsedIdx = fullCollapsed.text.indexOf(suffixCollapsed, suffixSearchStart);
    if (endCollapsedIdx === -1) continue;

    const startSource = fullCollapsed.sourceIndexByTextIndex[startCollapsedIdx];
    const endCollapsedExclusive = endCollapsedIdx + suffixCollapsed.length;
    const endSource =
      endCollapsedExclusive < fullCollapsed.sourceIndexByTextIndex.length
        ? fullCollapsed.sourceIndexByTextIndex[endCollapsedExclusive]
        : full.length;
    if (startSource == null || endSource == null || endSource <= startSource) continue;
    return { idx: startSource, len: endSource - startSource };
  }

  return null;
}

function findCollapsedSubstringRange(full, needle) {
  if (!needle) return null;
  const fullCollapsed = buildCollapsedSearchText(full);
  const needleCollapsed = buildCollapsedSearchText(stripMarkdownForHighlightSnippet(needle)).text.trim();
  if (needleCollapsed.length < 4) return null;
  const startCollapsedIdx = fullCollapsed.text.indexOf(needleCollapsed);
  if (startCollapsedIdx === -1) return null;
  const startSource = fullCollapsed.sourceIndexByTextIndex[startCollapsedIdx];
  const endCollapsedExclusive = startCollapsedIdx + needleCollapsed.length;
  const endSource =
    endCollapsedExclusive < fullCollapsed.sourceIndexByTextIndex.length
      ? fullCollapsed.sourceIndexByTextIndex[endCollapsedExclusive]
      : full.length;
  if (startSource == null || endSource == null || endSource <= startSource) return null;
  return { idx: startSource, len: endSource - startSource };
}

function findPunctuationInsensitiveRange(full, needle) {
  if (!needle) return null;
  const fullSearch = buildPunctuationInsensitiveSearchText(full);
  const needleSearch = buildPunctuationInsensitiveSearchText(stripMarkdownForHighlightSnippet(needle)).text.trim();
  if (needleSearch.length < 6) return null;
  const startIdx = fullSearch.text.indexOf(needleSearch);
  if (startIdx === -1) return null;
  const startSource = fullSearch.sourceIndexByTextIndex[startIdx];
  const endExclusive = startIdx + needleSearch.length;
  const endSource =
    endExclusive < fullSearch.sourceIndexByTextIndex.length
      ? fullSearch.sourceIndexByTextIndex[endExclusive]
      : full.length;
  if (startSource == null || endSource == null || endSource <= startSource) return null;
  return { idx: startSource, len: endSource - startSource };
}

/**
 * Find the first plain-text occurrence of inserted/edited prose in a ProseMirror document and return { from, to } positions.
 * Tries several search strings: the server sends plain-ish text, but we also try shorter prefixes when the insert is huge.
 * One O(n) walk of text nodes is reused for every candidate (grep + position map).
 */
/**
 * True when every text node in [from, to) carries the `inserted` mark (agent-applied prose after Yjs sync).
 */
export function docRangeHasInsertedMark(doc, from, to) {
  if (!doc || from >= to) return false;
  let sawText = false;
  let allInserted = true;
  doc.nodesBetween(from, to, (node) => {
    if (!node.isText || !node.text?.length) return;
    sawText = true;
    if (!node.marks.some((mark) => mark.type.name === "inserted")) {
      allInserted = false;
    }
  });
  return sawText && allInserted;
}

/** Contiguous PM ranges where every text node carries the `inserted` mark. */
export function collectInsertedMarkRanges(doc) {
  if (!doc) return [];
  const ranges = [];
  let open = null;
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text?.length) return;
    const hasInserted = node.marks.some((mark) => mark.type.name === "inserted");
    if (!hasInserted) {
      if (open) {
        ranges.push(open);
        open = null;
      }
      return;
    }
    const from = pos;
    const to = pos + node.text.length;
    if (open && from <= open.to + 1) {
      open.to = Math.max(open.to, to);
      return;
    }
    if (open) ranges.push(open);
    open = { from, to };
  });
  if (open) ranges.push(open);
  return ranges;
}

function normalizeHighlightCompareText(text) {
  return normalizeSnippetBlockSeparators(String(text ?? ""))
    .replace(/\s+/g, " ")
    .trim();
}

function scoreInsertedSpanAgainstSnippet(spanText, candidates) {
  let best = 0;
  const normalizedSpan = normalizeHighlightCompareText(spanText);
  for (const candidate of candidates) {
    const plain = stripMarkdownForHighlightSnippet(candidate);
    if (!plain || plain.length < 4) continue;
    const normalizedPlain = normalizeHighlightCompareText(plain);
    if (
      spanText.includes(plain)
      || plain.includes(spanText)
      || normalizedSpan.includes(normalizedPlain)
      || normalizedPlain.includes(normalizedSpan)
    ) {
      best = Math.max(best, Math.min(normalizedSpan.length, normalizedPlain.length));
      continue;
    }
    const collapsedSpan = buildCollapsedSearchText(normalizedSpan).text;
    const collapsedNeedle = buildCollapsedSearchText(normalizedPlain).text.trim();
    if (collapsedNeedle.length >= 4 && collapsedSpan.includes(collapsedNeedle)) {
      best = Math.max(best, collapsedNeedle.length);
    }
  }
  return best;
}

function highlightSnippetPlainLength(highlightSnippet) {
  return normalizeSnippetBlockSeparators(highlightSnippet).length;
}

function rangePlainText(doc, from, to) {
  return doc.textBetween(from, to, EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR);
}

function scoreRangeFitForHighlightSnippet(doc, range, targetPlainLen, candidates) {
  const rangeText = rangePlainText(doc, range.from, range.to);
  const textScore = scoreInsertedSpanAgainstSnippet(rangeText, candidates);
  const lengthPenalty = Math.abs(rangeText.length - targetPlainLen);
  return textScore * 20 - lengthPenalty;
}

function rangeAlignsWithHighlightSnippet(doc, from, to, highlightSnippet) {
  const snippetLen = highlightSnippetPlainLength(highlightSnippet);
  if (snippetLen < 4) return true;
  const rangeText = normalizeSnippetBlockSeparators(rangePlainText(doc, from, to));
  if (!rangeText) return false;
  const maxAllowed = Math.max(snippetLen + 80, Math.ceil(snippetLen * 1.25));
  if (rangeText.length > maxAllowed) return false;
  const candidates = buildHighlightSearchCandidates(highlightSnippet);
  const score = scoreInsertedSpanAgainstSnippet(rangeText, candidates);
  return score >= Math.min(40, Math.max(4, Math.floor(snippetLen * 0.3)));
}

/** True when [from, to) has no unmarked prose (only block edges / whitespace between inserted paragraphs). */
function gapHasNonInsertedProseText(doc, from, to) {
  if (from >= to) return false;
  let hasNonInserted = false;
  doc.nodesBetween(from, to, (node) => {
    if (!node.isText || !node.text?.length) return;
    if (!node.marks.some((mark) => mark.type.name === "inserted")) {
      hasNonInserted = true;
    }
  });
  return hasNonInserted;
}

/** Group inserted spans separated only by paragraph/block boundaries (same agent edit). */
function groupContiguousInsertedSpanGroups(doc, spans) {
  if (spans.length === 0) return [];
  const sorted = [...spans].sort((a, b) => a.from - b.from);
  const groups = [];
  let group = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = group[group.length - 1];
    const next = sorted[i];
    if (!gapHasNonInsertedProseText(doc, prev.to, next.from)) {
      group.push(next);
    } else {
      groups.push(group);
      group = [next];
    }
  }
  groups.push(group);
  return groups;
}

/** Smallest inserted span overlapping [from, to), or null. */
function minimalInsertedSpanOverlapping(doc, from, to, spans) {
  const insertedSpans = spans ?? collectInsertedMarkRanges(doc);
  const overlapping = insertedSpans.filter((span) => span.from < to && span.to > from);
  if (overlapping.length === 0) return null;
  return {
    from: Math.min(...overlapping.map((span) => span.from)),
    to: Math.max(...overlapping.map((span) => span.to)),
  };
}

/** Expand a substring match to cover every adjacent `inserted` mark in the document. */
export function expandRangeToInsertedMarkSpan(doc, from, to) {
  const spans = collectInsertedMarkRanges(doc);
  if (spans.length === 0) return { from, to };

  let selected = spans.filter((span) => span.from < to && span.to > from);
  if (selected.length === 0) {
    return { from, to };
  }

  let changed = true;
  while (changed) {
    changed = false;
    const minFrom = Math.min(...selected.map((span) => span.from));
    const maxTo = Math.max(...selected.map((span) => span.to));
    for (const span of spans) {
      if (selected.some((s) => s.from === span.from && s.to === span.to)) continue;
      const bridgesBefore = span.to <= minFrom && !gapHasNonInsertedProseText(doc, span.to, minFrom);
      const bridgesAfter = span.from >= maxTo && !gapHasNonInsertedProseText(doc, maxTo, span.from);
      if (bridgesBefore || bridgesAfter) {
        selected.push(span);
        changed = true;
      }
    }
  }

  return {
    from: Math.min(...selected.map((span) => span.from)),
    to: Math.max(...selected.map((span) => span.to)),
  };
}

/** PM range covering content inserted by a replace-at-range operation. */
export function pmRangeForReplacementInsert(docBefore, docAfter, replaceRange) {
  if (!docBefore || !docAfter || !replaceRange) return null;
  const from = Math.floor(replaceRange.from);
  const deleteLen = Math.max(0, Math.floor(replaceRange.to) - from);
  const insertedPmSize = docAfter.content.size - docBefore.content.size + deleteLen;
  const to = from + insertedPmSize;
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  return { from, to };
}

export function findAgentEditHighlightRange(doc, highlightSnippet) {
  if (!highlightSnippet?.trim() || !doc) return null;

  const trimmedSnippet = highlightSnippet.trim();
  const insertedSpans = collectInsertedMarkRanges(doc);
  const candidates = buildHighlightSearchCandidates(highlightSnippet);
  const targetLen = highlightSnippetPlainLength(highlightSnippet);
  const minInsertedGroupScore = Math.min(40, Math.max(4, Math.floor(targetLen * 0.3)));

  if (trimmedSnippet.length < 4 && insertedSpans.length > 0) {
    const hits = insertedSpans
      .filter((span) => doc.textBetween(span.from, span.to).includes(trimmedSnippet))
      .sort((a, b) => a.to - a.from - (b.to - b.from));
    if (hits.length > 0) return hits[0];
  }

  const textRange = findFirstTextRangeInDoc(doc, highlightSnippet);

  if (insertedSpans.length > 0) {
    const rangeCandidates = insertedSpans.map((span) => ({ from: span.from, to: span.to }));
    for (const group of groupContiguousInsertedSpanGroups(doc, insertedSpans)) {
      rangeCandidates.push({
        from: Math.min(...group.map((span) => span.from)),
        to: Math.max(...group.map((span) => span.to)),
      });
    }
    if (textRange) rangeCandidates.push(textRange);

    let best = null;
    let bestScore = -1;
    for (const candidate of rangeCandidates) {
      const score = scoreRangeFitForHighlightSnippet(doc, candidate, targetLen, candidates);
      if (score > bestScore) {
        bestScore = score;
        best = candidate;
      }
    }
    if (best && bestScore >= minInsertedGroupScore) {
      if (trimmedSnippet.length < 16) {
        const minimal = minimalInsertedSpanOverlapping(
          doc,
          best.from,
          best.to,
          insertedSpans,
        );
        if (minimal) return minimal;
      }
      return best;
    }
    if (insertedSpans.length === 1) return insertedSpans[0];
  }

  if (textRange) {
    const expanded = expandRangeToInsertedMarkSpan(doc, textRange.from, textRange.to);
    if (rangeAlignsWithHighlightSnippet(doc, expanded.from, expanded.to, highlightSnippet)) {
      return expanded;
    }
    if (rangeAlignsWithHighlightSnippet(doc, textRange.from, textRange.to, highlightSnippet)) {
      return textRange;
    }
    const minimal = minimalInsertedSpanOverlapping(doc, textRange.from, textRange.to, insertedSpans);
    if (minimal) return minimal;
    return textRange;
  }

  return null;
}

export function findFirstTextRangeInDoc(doc, searchText) {
  if (
    !searchText
    || !doc
    || typeof doc.descendants !== "function"
    || typeof doc.textBetween !== "function"
    || typeof doc.textContent !== "string"
    || !Number.isFinite(doc.content?.size)
  ) {
    return null;
  }
  const built = buildTextAndPositions(doc);
  const candidates = buildHighlightSearchCandidates(searchText);
  const targetPlainLen = highlightSnippetPlainLength(searchText);
  const matches = [];
  const blockAware = findFirstTextRangeInDocBlockAware(doc, searchText);
  if (blockAware) matches.push(blockAware);
  for (const c of candidates) {
    const r = findFirstTextRangeInDocExact(doc, c, built);
    if (r) matches.push(r);
  }
  let best = null;
  let bestScore = -1;
  for (const range of matches) {
    const score = scoreRangeFitForHighlightSnippet(doc, range, targetPlainLen, candidates);
    if (score > bestScore) {
      best = range;
      bestScore = score;
    }
  }
  return best;
}

function findFirstTextRangeInDocExact(doc, searchText, built) {
  if (!searchText || !doc) return null;
  const { full, beforePos, ok } = built;
  if (ok) {
    const m = findSubstringMatch(full, searchText);
    if (m) {
      const { idx, len } = m;
      if (idx + len > full.length) return null;
      const from = beforePos[idx];
      const to = idx + len < full.length ? beforePos[idx + len] : charIndexToLargestContentPos(doc, full.length);
      if (from != null && to != null && from < to) {
        return { from, to };
      }
    }
  }

  return findFirstTextRangeInDocViaBinarySearch(doc, searchText);
}

function findFirstTextRangeInDocViaBinarySearch(doc, searchText) {
  if (!doc || typeof doc.textContent !== "string") return null;
  const full = doc.textContent;
  let m = findSubstringMatch(full, searchText);
  if (!m) {
    m = findCollapsedSubstringRange(full, searchText);
  }
  if (!m) {
    m = findPunctuationInsensitiveRange(full, searchText);
  }
  if (!m) {
    m = findAnchoredLongRange(full, searchText);
  }
  if (!m) {
    if (searchText.length > 400) {
      return findFirstTextRangeInDocViaBinarySearch(doc, searchText.slice(0, 400));
    }
    return null;
  }
  const { idx, len } = m;
  const end = idx + len;
  const from = charIndexToLargestContentPos(doc, idx);
  const to = charIndexToLargestContentPos(doc, end);
  if (from == null || to == null || from >= to) return null;
  return { from, to };
}

/**
 * Map a character offset in `doc.textContent` to a **doc content** position (same coords as
 * `Decoration.inline` / `doc.textBetween`). Used when the text-node walk cannot be used or for
 * the exclusive end when the match runs to the end of the document.
 */
function charIndexToLargestContentPos(doc, charIndex) {
  const full = doc.textContent;
  if (charIndex < 0 || charIndex > full.length) return null;
  let lo = 0;
  let hi = doc.content.size + 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const len = mid <= doc.content.size ? doc.textBetween(0, mid).length : full.length + 1;
    if (len > charIndex) hi = mid;
    else lo = mid + 1;
  }
  const p = lo - 1;
  if (p < 0 || p > doc.content.size) return null;
  if (doc.textBetween(0, p).length !== charIndex) return null;
  return p;
}
